import { apiUrl } from "../lib/apiBase";

export type SupplementRecord = {
  id: string;
  claimNumber?: string;
  insuredName?: string;
  carrierName?: string;
  lossAddress?: string;
  itemCount: number;
  total: number;
  completedAt: number;
  packetId?: string;
  packetVersion?: string;
};

const SUPPLEMENTS_KEY = "claimsupp:supplements:v1";

function canUseStorage(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

export function saveSupplements(list: SupplementRecord[]): void {
  if (!canUseStorage()) return;
  window.localStorage.setItem(SUPPLEMENTS_KEY, JSON.stringify(list));
}

function loadLocalSupplements(): SupplementRecord[] {
  if (!canUseStorage()) return [];
  try {
    const raw = window.localStorage.getItem(SUPPLEMENTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as SupplementRecord[]) : [];
  } catch {
    return [];
  }
}

export async function loadSupplements(): Promise<SupplementRecord[]> {
  try {
    const res = await fetch(apiUrl("/supplements"), { credentials: "include" });
    const payload = await res.json().catch(() => null);
    if (!res.ok) throw new Error(payload?.error || "supplements_load_failed");
    const list = Array.isArray(payload?.records) ? payload.records : [];
    saveSupplements(list);
    return list;
  } catch {
    return loadLocalSupplements();
  }
}

export async function upsertSupplement(record: SupplementRecord): Promise<SupplementRecord[]> {
  try {
    const res = await fetch(apiUrl("/supplements"), {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ record }),
    });
    const payload = await res.json().catch(() => null);
    if (!res.ok) throw new Error(payload?.error || "supplements_save_failed");
    const list = Array.isArray(payload?.records) ? payload.records : [];
    saveSupplements(list);
    return list;
  } catch {
    const list = loadLocalSupplements();
    const idx = list.findIndex((item) => item.id === record.id);
    if (idx >= 0) list[idx] = { ...list[idx], ...record };
    else list.unshift(record);
    saveSupplements(list);
    return list;
  }
}
